import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Button, 
  Space, 
  Tabs,
  Input,
  DatePicker,
  Select,
  message,
  Spin
} from 'antd';
import { 
  LogoutOutlined, 
  PlusOutlined, 
  SortAscendingOutlined,
  ClearOutlined
} from '@ant-design/icons';
import ActiveTasksTable from '../components/ActiveTasksTable';
import HistoryTasksTable from '../components/HistoryTasksTable';
import AddTaskModal from '../components/AddTaskModal';
import EditTaskModal from '../components/EditTaskModal';
import materials from '../config/materials';
import moment from 'moment';
// 导入API服务
import { taskService } from '../services/apiService';
// 导入可靠的UUID库来生成唯一ID
import { v4 as uuidv4 } from 'uuid';

// 更可靠的ID生成方法
const generateUniqueId = () => {
  return uuidv4();
};

const { Header, Content } = Layout;
const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

// 最大历史任务数量常量
const MAX_HISTORY_TASKS = 100;

const Dashboard = ({ user, onLogout }) => {
  // States
  const [tasks, setTasks] = useState([]);
  const [historyTasks, setHistoryTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSorting, setIsSorting] = useState(false);
  const [originalTasksOrder, setOriginalTasksOrder] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [materialFilter, setMaterialFilter] = useState(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);

  // 加载任务数据
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // 获取任务和历史任务
        const [taskData, historyData] = await Promise.all([
          taskService.getTasks(),
          taskService.getHistoryTasks()
        ]);
        
        setTasks(taskData || []);
        setHistoryTasks(historyData || []);
      } catch (error) {
        console.error('Error loading data:', error);
        message.error('Failed to load tasks from server.');
        setTasks([]);
        setHistoryTasks([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  // 处理完成任务
  const handleCompleteTask = async (taskId) => {
    try {
      setIsSaving(true);
      const response = await taskService.completeTask(taskId);
      
      if (response.success) {
        // 更新本地状态
        const taskIndex = tasks.findIndex(task => task.id === taskId);
        if (taskIndex !== -1) {
          const updatedTasks = tasks.filter(task => task.id !== taskId);
          setTasks(updatedTasks);
          
          // 更新历史任务 - 由服务器处理，所以需要重新获取
          const historyData = await taskService.getHistoryTasks();
          setHistoryTasks(historyData);
        }
        message.success('Task marked as completed');
      } else {
        message.error(response.message || 'Failed to complete task');
      }
    } catch (error) {
      console.error('Error completing task:', error);
      message.error('Failed to complete task');
    } finally {
      setIsSaving(false);
    }
  };

  // 处理删除任务
  const handleDeleteTask = async (taskId) => {
    console.log('Delete button clicked for task ID:', taskId);
    try {
      setIsSaving(true);
      
      try {
        const response = await taskService.deleteTask(taskId);
        console.log('Delete response:', response);
        
        if (response.success) {
          // 更新本地状态
          const updatedTasks = tasks.filter(task => task.id !== taskId);
          setTasks(updatedTasks);
          
          // 更新历史任务 - 由服务器处理，所以需要重新获取
          const historyData = await taskService.getHistoryTasks();
          setHistoryTasks(historyData);
          
          message.success('Task deleted');
        } else {
          throw new Error(response.message || 'Failed to delete task on server');
        }
      } catch (error) {
        console.error('Server error:', error);
        
        // 即使服务器操作失败，也更新UI以提高用户体验
        const updatedTasks = tasks.filter(task => task.id !== taskId);
        setTasks(updatedTasks);
        
        message.warning('Task removed from view, but server update failed');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      message.error('Failed to delete task');
    } finally {
      setIsSaving(false);
    }
  };

  // 处理编辑任务
  const handleEditTask = (task) => {
    setCurrentTask(task);
    setIsEditModalVisible(true);
  };

  // 处理保存编辑的任务
  const handleSaveTask = async (updatedTask) => {
    try {
      setIsSaving(true);
      
      // 确保业务规则：只有材料状态为 "In Stock" 时才能将作业状态设置为 "Started"
      if (updatedTask.jobStatus === "red" && updatedTask.materialStatus !== "in stock") {
        message.error('Cannot start job: material is not in stock');
        setIsSaving(false);
        return;
      }
      
      // 更新任务列表
      const updatedTasks = tasks.map(task => 
        task.id === updatedTask.id ? updatedTask : task
      );
      
      // 重新应用排序规则：优先级任务始终位于顶部
      const prioritizedTasks = updatedTasks.filter(task => task.jobStatus === "red");
      const normalTasks = updatedTasks.filter(task => task.jobStatus !== "red");
      const reorderedTasks = [...prioritizedTasks, ...normalTasks];
      
      // 保存到服务器
      await taskService.updateTasks(reorderedTasks);
      
      // 更新本地状态
      setTasks(reorderedTasks);
      setIsEditModalVisible(false);
      setCurrentTask(null);
      
      message.success('Task updated successfully');
    } catch (error) {
      console.error('Error saving task:', error);
      message.error('Failed to save task');
    } finally {
      setIsSaving(false);
    }
  };

  // 开始排序模式
  const startSorting = () => {
    setIsSorting(true);
    setOriginalTasksOrder([...tasks]);
    message.info('Drag tasks to reorder them (Started tasks remain on top)');
  };

  // 取消排序
  const cancelSorting = () => {
    setTasks([...originalTasksOrder]);
    setIsSorting(false);
    message.info('Sorting canceled');
  };

  // 确认排序
  const confirmSorting = async () => {
    try {
      setIsSaving(true);
      
      // 保存排序后的任务到服务器
      await taskService.updateTasks(tasks);
      
      setIsSorting(false);
      message.success('New order saved');
    } catch (error) {
      console.error('Error saving sort order:', error);
      message.error('Failed to save new order');
      // 还原到原始顺序
      setTasks([...originalTasksOrder]);
    } finally {
      setIsSaving(false);
    }
  };

  // 处理任务重新排序 - 确保优先任务始终在顶部
  const handleTasksReorder = (reorderedTasks) => {
    // 分离优先任务和普通任务
    const prioritizedTasks = reorderedTasks.filter(task => task.jobStatus === "red");
    const normalTasks = reorderedTasks.filter(task => task.jobStatus !== "red");
    
    // 重新组合任务，优先任务始终在顶部
    setTasks([...prioritizedTasks, ...normalTasks]);
  };

  // 清除所有筛选器
  const clearFilters = () => {
    setSearchText('');
    setDateFilter(null);
    setMaterialFilter(null);
    message.info('Filters cleared');
  };

  // 添加新任务
  // 添加新任务 - 修改后的版本
const handleAddTask = async (taskData) => {
  try {
    setIsSaving(true);
    
    // 确保任务数据包含所有必要的默认值
    const newTask = {
      jobNumber: taskData.jobNumber,
      customer: taskData.customer,
      material: taskData.material || "-",
      materialQuantity: taskData.materialQuantity || "-",
      materialStatus: taskData.materialStatus || "-",
      machine: taskData.machine || "-",
      jobStatus: taskData.jobStatus || "yellow", // 默认为等待状态
      id: generateUniqueId(),
      timestamp: new Date().toISOString()
    };
    
    // 添加任务到服务器
    const response = await taskService.addTask(newTask);
    
    if (response.success) {
      // 维持任务优先级顺序
      const updatedTasks = [...tasks, newTask];
      const prioritizedTasks = updatedTasks.filter(task => task.jobStatus === "red");
      const normalTasks = updatedTasks.filter(task => task.jobStatus !== "red");
      const reorderedTasks = [...prioritizedTasks, ...normalTasks];
      
      // 更新本地状态
      setTasks(reorderedTasks);
      setIsAddModalVisible(false);
      message.success('New task added');
    } else {
      message.error(response.message || 'Failed to add task');
    }
  } catch (error) {
    console.error('Error adding new task:', error);
    message.error('Failed to add new task');
  } finally {
    setIsSaving(false);
  }
};

  // 根据搜索和筛选条件过滤任务
  const getFilteredTasks = () => {
    let filteredTasks = [...tasks];
    
    // 应用搜索文本过滤
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filteredTasks = filteredTasks.filter(task => 
        (task.jobNumber && task.jobNumber.toLowerCase().includes(searchLower)) || 
        (task.customer && task.customer.toLowerCase().includes(searchLower))
      );
    }
    
    // 应用日期过滤
    if (dateFilter) {
      const filterDateStr = dateFilter.format('YYYY-MM-DD');
      filteredTasks = filteredTasks.filter(task => {
        // 确保时间戳存在且有效
        if (!task.timestamp) return false;
        
        const taskDate = moment(task.timestamp);
        return taskDate.isValid() && taskDate.format('YYYY-MM-DD') === filterDateStr;
      });
    }
    
    // 应用材料过滤
    if (materialFilter) {
      filteredTasks = filteredTasks.filter(task => task.material === materialFilter);
    }
    
    return filteredTasks;
  };

  // 显示加载状态
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Loading tasks..." />
      </div>
    );
  }

  return (
    <Layout className="layout">
      <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Title level={4} style={{ color: 'white', margin: 0 }}>
          Laser Schedule Management
        </Title>
        <Space>
          <span style={{ color: 'white' }}>
            User: {user}
          </span>
          <Button 
            type="primary" 
            icon={<LogoutOutlined />} 
            onClick={onLogout}
            danger
          >
            Logout
          </Button>
        </Space>
      </Header>
      
      <Content style={{ padding: '24px 50px' }}>
        <div className="site-layout-content">
          <div className="table-operations">
            <Space>
              <Search
                placeholder="Search job number or customer"
                allowClear
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 250 }}
              />
              <DatePicker 
                placeholder="Filter by date" 
                value={dateFilter}
                onChange={setDateFilter}
              />
              <Select
                placeholder="Filter by material"
                allowClear
                style={{ width: 200 }}
                value={materialFilter}
                onChange={setMaterialFilter}
              >
                {materials.map(material => (
                  <Option key={material.id} value={material.id}>{material.name}</Option>
                ))}
              </Select>
              <Button 
                icon={<ClearOutlined />} 
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            </Space>
            
            <div>
              {!isSorting ? (
                <>
                  <Button 
                    type="primary" 
                    icon={<SortAscendingOutlined />} 
                    onClick={startSorting}
                    disabled={isSaving}
                  >
                    Start Sorting
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={() => setIsAddModalVisible(true)}
                    disabled={isSaving}
                  >
                    Add New Task
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    type="primary" 
                    onClick={confirmSorting}
                    loading={isSaving}
                  >
                    Confirm Sort
                  </Button>
                  <Button 
                    danger 
                    onClick={cancelSorting}
                    disabled={isSaving}
                  >
                    Cancel Sort
                  </Button>
                </>
              )}
            </div>
          </div>
          
          <Tabs 
            defaultActiveKey="active"
            items={[
              {
                key: 'active',
                label: 'Active Tasks',
                children: (
                  <ActiveTasksTable
                    tasks={getFilteredTasks()}
                    onComplete={handleCompleteTask}
                    onDelete={handleDeleteTask}
                    onEdit={handleEditTask}
                    isSorting={isSorting}
                    onReorder={handleTasksReorder}
                    materials={materials}
                    loading={isSaving}
                  />
                ),
              },
              {
                key: 'history',
                label: 'Order History',
                children: (
                  <HistoryTasksTable
                    tasks={historyTasks}
                    materials={materials}
                    loading={isSaving}
                  />
                ),
              },
            ]}
          />
        </div>
      </Content>
      
      <AddTaskModal
        visible={isAddModalVisible}
        onAdd={handleAddTask}
        onCancel={() => setIsAddModalVisible(false)}
        confirmLoading={isSaving}
      />

      <EditTaskModal
        visible={isEditModalVisible}
        onSave={handleSaveTask}
        onCancel={() => {
          setIsEditModalVisible(false);
          setCurrentTask(null);
        }}
        task={currentTask}
        materials={materials}
        confirmLoading={isSaving}
      />
    </Layout>
  );
};

export default Dashboard;